import { memo, type FC, useRef } from 'react';
import { useEffectOnce } from 'react-use';
import { Provider } from 'react-redux';
import { FrontRoyalRouter } from 'FrontRoyalRouter';
import { angularInjectorProvider } from 'Injector';
import { initializeAppStore } from 'FrontRoyalRedux';
import { useWaitFor } from 'useWaitFor';
import { InitStatsigClient } from 'FrontRoyalStatsig';
import { ReactWrapper } from './ReactWrapper';
import 'FrontRoyalAngular/angularModule';

const store = initializeAppStore();

function useWaitForAngularInjector() {
    return useWaitFor(() => angularInjectorProvider.injector);
}

function FrontRoyalContent() {
    const angularInjector = useWaitForAngularInjector();

    if (!angularInjector) return null;

    // provideHeroUi is false because the HeroUIProvider is set up inside of FrontRoyalRouter so that we can
    // support our custom navigation rules
    return (
        <InitStatsigClient>
            <ReactWrapper angularInjector={angularInjector} store={store} provideHeroUi={false}>
                <FrontRoyalRouter />
            </ReactWrapper>
        </InitStatsigClient>
    );
}

const FrontRoyalRootComponent: FC = () => {
    const ngRootRef = useRef<HTMLDivElement | null>(null);

    useEffectOnce(() => {
        const ngRootEl = ngRootRef.current;
        if (!ngRootEl) {
            throw new Error('No ngRootEl');
        }

        // Bootstrap Angular - the run block will set angularInjectorProvider.injector
        angular.bootstrap(ngRootEl, ['FrontRoyal']);

        // Remove initial loading spinner
        $('.loading-front-royal').remove();
    });

    return (
        <Provider store={store}>
            {/* In order for ng-animate to work, the ng-root has to wrap the site's content. If
             we removed all usages of ng-animate, and stopped using toast, then ng-root could just be an empty div */}
            <div id="ng-root" ref={ngRootRef}>
                {
                    // @ts-expect-error Legacy Angular directive that still renders toasts
                    <toast className="animated" />
                }
                <FrontRoyalContent />
            </div>
        </Provider>
    );
};

export const FrontRoyalRoot = memo(FrontRoyalRootComponent) as typeof FrontRoyalRootComponent;
